import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Verified, Star, MapPin, Bitcoin, Search, Globe, Instagram, Send, MessageCircle, Mail, Phone, ArrowLeft, Share2, Check, Clock } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { useFx } from "../lib/fx";
import { useI18n } from "../lib/i18n";
import { formatServiceDuration } from "../lib/serviceMeta";

export default function ProviderPublicPage() {
  const { slug } = useParams();
  const { t } = useI18n();
  const { format } = useFx();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setData(null); setError(null);
        const { data } = await api.get(`/providers/public/${slug}`);
        setData(data);
      } catch (e) { setError(formatErr(e)); }
    })();
  }, [slug]);

  const sharable = typeof window !== "undefined" ? `${window.location.origin}/p/${slug}` : "";

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.groups
      .filter((g) => activeCat === "all" || g.category.id === activeCat)
      .map((g) => ({
        ...g,
        services: g.services.filter(
          (s) =>
            !q ||
            (s.title || "").toLowerCase().includes(q) ||
            (s.description || "").toLowerCase().includes(q) ||
            (s.location || "").toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.services.length > 0);
  }, [data, activeCat, query]);

  if (error) return (
    <div className="pt-32 px-6 max-w-3xl mx-auto" data-testid="public-provider-error">
      <div className="text-rose-300 mb-4">{error}</div>
      <Link to="/" className="btn btn-outline">{t("common.back", "Home")}</Link>
    </div>
  );
  if (!data) return <div className="pt-32 px-6 text-muted">{t("common.loading", "Loading…")}</div>;

  const p = data.provider;
  const dests = data.destinations || [];
  const allCats = data.groups.map((g) => g.category);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(sharable);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  return (
    <div className="pb-24" data-testid="public-provider-page">
      {/* Hero */}
      <header className="relative pt-28 pb-14 px-5 md:px-10 overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-40"
          style={{
            background:
              "radial-gradient(900px 480px at 30% 20%, rgba(34,232,213,0.20), transparent 60%), radial-gradient(700px 400px at 80% 60%, rgba(192,132,252,0.18), transparent 60%)",
          }}
        />
        <div className="mx-auto max-w-6xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-muted hover:text-teal text-sm mb-6"
            data-testid="public-provider-home-link"
          >
            <ArrowLeft size={14} /> {t("common.back", "Home")}
          </Link>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-16 w-16 rounded-2xl bg-teal/15 grid place-items-center text-teal font-serif-display text-3xl">
                  {p.name?.[0]}
                </div>
                <div>
                  <div className="eyebrow">{t("public_provider.eyebrow", "Verified provider")}</div>
                  <h1 className="section-title text-4xl md:text-5xl mt-1 flex items-center gap-3">
                    {p.name}
                    {p.verified && <Verified className="text-teal shrink-0" size={26} />}
                  </h1>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-sm text-muted">
                {p.rating_count > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <Star size={14} className="fill-champagne text-champagne" />
                    {t("provider_dash.rating_summary", "{{avg}} avg · {{count}} reviews", {
                      avg: p.rating_avg,
                      count: p.rating_count,
                    })}
                  </span>
                )}
                {dests.length > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin size={14} />
                    {dests.map((d) => d.name).join(" · ")}
                  </span>
                )}
                <span className="chip">
                  {t("public_provider.service_count", "{{count}} services", {
                    count: data.service_count,
                  })}
                </span>
                <span className="chip success">
                  <Bitcoin size={11} /> {t("public_provider.crypto_ready", "Crypto-ready")}
                </span>
              </div>

              {p.description && (
                <p className="mt-5 text-base leading-relaxed text-bone/85 max-w-2xl">
                  {p.description}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2 md:items-end">
              <button
                onClick={onCopy}
                className="btn btn-primary"
                data-testid="public-provider-share"
              >
                {copied ? <Check size={14} /> : <Share2 size={14} />}{" "}
                {copied
                  ? t("public_provider.share_copied", "Link copied")
                  : t("public_provider.share_link", "Share my page")}
              </button>
              {sharable && (
                <code className="text-[11px] text-muted/70 font-mono break-all max-w-xs text-right">
                  {sharable}
                </code>
              )}
            </div>
          </div>

          {/* Contact pills */}
          {(p.contact_email || p.contact_phone || p.whatsapp || p.telegram || p.instagram || p.website) && (
            <div className="mt-7 flex flex-wrap gap-2" data-testid="public-provider-contact">
              {p.website && (
                <ExtLink href={p.website} icon={<Globe size={13} />} label={t("public_provider.website", "Website")} testid="contact-website" />
              )}
              {p.contact_email && (
                <ExtLink href={`mailto:${p.contact_email}`} icon={<Mail size={13} />} label={p.contact_email} testid="contact-email" />
              )}
              {p.contact_phone && (
                <ExtLink href={`tel:${p.contact_phone}`} icon={<Phone size={13} />} label={p.contact_phone} testid="contact-phone" />
              )}
              {p.whatsapp && (
                <ExtLink
                  href={`https://wa.me/${p.whatsapp.replace(/[^0-9]/g, "")}`}
                  icon={<MessageCircle size={13} />}
                  label="WhatsApp"
                  testid="contact-whatsapp"
                />
              )}
              {p.telegram && (
                <ExtLink
                  href={`https://t.me/${p.telegram.replace(/^@/, "")}`}
                  icon={<Send size={13} />}
                  label="Telegram"
                  testid="contact-telegram"
                />
              )}
              {p.instagram && (
                <ExtLink
                  href={`https://instagram.com/${p.instagram.replace(/^@/, "")}`}
                  icon={<Instagram size={13} />}
                  label={p.instagram}
                  testid="contact-instagram"
                />
              )}
            </div>
          )}
        </div>
      </header>

      {/* Filters */}
      <section className="px-5 md:px-10 mx-auto max-w-6xl">
        <div className="luxe-card p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3" data-testid="public-provider-filters">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("public_provider.search_placeholder", "Search villas, yachts, experiences…")}
              className="input-bare !pl-10"
              data-testid="public-provider-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActiveCat("all")}
              className={`tab-pill ${activeCat === "all" ? "active" : ""}`}
              data-testid="catf-all"
            >
              {t("public_provider.all_categories", "All")}
            </button>
            {allCats.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveCat(c.id)}
                className={`tab-pill ${activeCat === c.id ? "active" : ""}`}
                data-testid={`catf-${c.slug}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grouped services */}
      <main className="px-5 md:px-10 mx-auto max-w-6xl mt-10 space-y-14">
        {filtered.length === 0 ? (
          <div className="luxe-card p-10 text-center text-muted text-sm">
            {t("public_provider.no_services_match", "No services match your filters yet.")}
          </div>
        ) : (
          filtered.map((g) => (
            <section key={g.category.id} data-testid={`group-${g.category.slug}`}>
              <div className="flex items-baseline justify-between gap-3 mb-4">
                <div>
                  <div className="eyebrow">{g.category.name}</div>
                  <h2 className="font-serif-display text-3xl mt-1">
                    {t("public_provider.section_offering", "{{name}} offering", { name: g.category.name })}
                  </h2>
                </div>
                <span className="text-xs text-muted">
                  {t("public_provider.section_count", "{{count}} listed", { count: g.services.length })}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {g.services.map((s) => (
                  <ServiceCardPublic key={s.id} s={s} format={format} t={t} />
                ))}
              </div>
            </section>
          ))
        )}
      </main>
    </div>
  );
}

function ExtLink({ href, icon, label, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="chip hover:border-teal/60 hover:text-teal transition-colors"
      data-testid={testid}
    >
      {icon} {label}
    </a>
  );
}

function ServiceCardPublic({ s, format, t }) {
  const cover = s.images?.[0] || "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";
  const duration = formatServiceDuration(s, t);
  return (
    <Link
      to={`/services/${s.id}`}
      className="luxe-card flex flex-col group"
      data-testid={`pub-svc-${s.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={cover}
          alt={s.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {s.is_featured && (
          <span className="absolute top-3 left-3 chip info">
            {t("service_detail.featured", "Featured")}
          </span>
        )}
        {s.crypto_accepted && (
          <span className="absolute top-3 right-3 chip">
            <Bitcoin size={11} /> {t("service_detail.crypto", "Crypto")}
          </span>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="font-serif-display text-xl line-clamp-2">{s.title}</div>
        {s.location && (
          <div className="mt-1 text-xs text-muted inline-flex items-center gap-1.5">
            <MapPin size={11} /> {s.location}
          </div>
        )}
        {duration && (
          <div className="mt-1 text-xs text-muted inline-flex items-center gap-1.5">
            <Clock size={11} /> {duration}
          </div>
        )}
        <p className="mt-2 text-sm text-muted line-clamp-2 flex-1">{s.description}</p>
        <div className="mt-4 flex items-baseline justify-between">
          <div>
            <div className="label-tiny">{t("common.from", "From")}</div>
            <div className="font-serif-display text-2xl">{format(s.base_price, s.currency)}</div>
          </div>
          <span className="btn btn-ghost text-[12px] !px-2 group-hover:text-teal">
            {t("public_provider.view_details", "View")} →
          </span>
        </div>
      </div>
    </Link>
  );
}
